import {
  endOfDay,
  getHours,
  getMilliseconds,
  getMinutes,
  getSeconds,
  isEqual,
  isSameDay,
  set,
  startOfDay,
} from 'date-fns'
import omit from 'lodash-es/omit'

import { isEqualNullable } from '../../../utils/common'
import {
  createDayWithNearestAfterCurrentTime,
  createTimeFrom,
} from '../../../utils/date'
import { InputWrap } from '../../Input'
import { DatePicker } from '../DatePicker'
import { TimePicker } from '../TimePicker'

import * as styles from './DateTimePicker.module.css'

function DateTimePicker({
  Component = InputWrap,
  Date = DatePicker,
  Time = TimePicker,
  borderless,
  children,
  className = styles.DateTimePicker,
  clearable = true,
  createDate = createDayWithNearestAfterCurrentTime,
  dateProps,
  disabled,
  errorText,
  maxValue,
  minValue,
  separator,
  separatorClassName = styles.separator,
  showError,
  timeProps,
  value,
  onChange,
  ...props
}) {
  const componentProps = omit(props, ['onClear'])

  function handleDatePickerChange(nextDate) {
    const nextValue =
      value == null
        ? nextDate
        : nextDate == null
          ? null
          : set(nextDate, {
              hours: getHours(value),
              milliseconds: getMilliseconds(value),
              minutes: getMinutes(value),
              seconds: getSeconds(value),
            })

    if (!isEqualNullable(nextValue, value, isEqual)) {
      onChange?.(nextValue)
    }
  }

  function handleTimePickerChange(nextTime) {
    const nextValue =
      value == null
        ? null
        : nextTime == null
          ? value
          : set(value, {
              hours: getHours(nextTime),
              milliseconds: getMilliseconds(nextTime),
              minutes: getMinutes(nextTime),
              seconds: getSeconds(nextTime),
            })

    if (!isEqualNullable(nextValue, value, isEqual)) {
      onChange?.(nextValue)
    }
  }

  const dateValue = value == null ? null : startOfDay(value)

  const maxDateValue = maxValue == null ? null : endOfDay(maxValue)

  const minDateValue = minValue == null ? null : startOfDay(minValue)

  const timeValue = value == null ? null : createTimeFrom(value)

  const maxTimeValue =
    value != null && maxValue != null && isSameDay(value, maxValue)
      ? createTimeFrom(maxValue)
      : null

  const minTimeValue =
    value != null && minValue != null && isSameDay(value, minValue)
      ? createTimeFrom(minValue)
      : null

  return (
    <Component {...componentProps} className={className} error={errorText}>
      <div className={styles.content}>
        <Date
          {...dateProps}
          borderless={borderless}
          clearable={clearable}
          createDate={createDate}
          disabled={disabled}
          maxValue={maxDateValue}
          minValue={minDateValue}
          showError={showError || !!errorText}
          value={dateValue}
          onChange={handleDatePickerChange}
        />
        {separator ? (
          <div className={separatorClassName}>{separator}</div>
        ) : null}
        <Time
          {...timeProps}
          borderless={borderless}
          clearable={false}
          disabled={disabled || value == null}
          maxValue={maxTimeValue}
          minValue={minTimeValue}
          showError={showError || !!errorText}
          value={timeValue}
          onChange={handleTimePickerChange}
        />
        {children}
      </div>
    </Component>
  )
}

export default DateTimePicker
